<div class="non-scrap-transactions-container">
  <!-- Header -->
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Non Scrap Transactions</h1>
        <p>View and manage non-scrap item transactions</p>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div class="filters-section">
    <div class="filters-grid">
      <div class="filter-group">
        <label for="agentFilter">Agent:</label>
        <select 
          id="agentFilter" 
          [(ngModel)]="selectedAgentId" 
          (change)="onFilterChange()"
          class="filter-select">
          <option value="">All Agents</option>
          <option *ngFor="let agent of agents" [value]="agent.agentId">
            {{ agent.name }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label for="zoneFilter">Zone:</label>
        <select 
          id="zoneFilter" 
          [(ngModel)]="selectedZoneId" 
          (change)="onFilterChange()"
          class="filter-select">
          <option value="">All Zones</option>
          <option *ngFor="let zone of zones" [value]="zone.id">
            {{ zone.name }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label for="settledFilter">Settlement Status:</label>
        <select 
          id="settledFilter" 
          [(ngModel)]="selectedIsSettled" 
          (change)="onFilterChange()"
          class="filter-select">
          <option value="">All</option>
          <option value="true">Settled</option>
          <option value="false">Not Settled</option>
        </select>
      </div>

      <div class="filter-group">
        <label for="fromDate">From Date:</label>
        <input 
          type="date" 
          id="fromDate" 
          [(ngModel)]="fromDate" 
          (change)="onFilterChange()"
          class="filter-select">
      </div>

      <div class="filter-group">
        <label for="toDate">To Date:</label>
        <input 
          type="date" 
          id="toDate" 
          [(ngModel)]="toDate" 
          (change)="onFilterChange()"
          class="filter-select">
      </div>

      <div class="filter-actions">
        <button 
          class="clear-filters-btn" 
          (click)="clearFilters()"
          [disabled]="loading">
          <i class="fas fa-times"></i> Clear Filters
        </button>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage" class="error-message">
    <i class="fas fa-exclamation-circle mr-2"></i>
    {{ errorMessage }}
  </div>

  <!-- Success Message -->
  <div *ngIf="successMessage" class="success-message">
    <i class="fas fa-check-circle mr-2"></i>
    {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <div class="loading-spinner"></div>
    <span class="loading-text">Loading non-scrap transactions...</span>
  </div>

  <!-- Results Summary -->
  <div *ngIf="!loading && transactions.length > 0" class="results-summary">
    <p>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} transactions</p>
  </div>

  <!-- Bulk Settlement Actions -->
  <div *ngIf="!loading && transactions.length > 0" class="bulk-actions-section">
    <div class="bulk-actions-header">
      <div class="selection-info">
        <span *ngIf="selectedTransactionsCount > 0" class="selection-count">
          {{ selectedTransactionsCount }} transaction(s) selected
          <span *ngIf="selectedTransactionsCount > 0" class="selected-amount">
            (Total: {{ formatCurrency(selectedTransactionsTotalAmount) }})
          </span>
        </span>
        <span *ngIf="selectedTransactionsCount === 0" class="no-selection">
          Select transactions to update settlement
        </span>
      </div>
      <div class="bulk-actions-buttons">
        <button 
          class="select-all-btn" 
          (click)="selectAllTransactions()"
          [disabled]="loading || allSelectableTransactionsSelected || selectableTransactionsCount === 0">
          <i class="fas fa-check-square"></i> Select All
        </button>
        <button 
          class="clear-selection-btn" 
          (click)="clearSelection()"
          [disabled]="loading || selectedTransactionsCount === 0">
          <i class="fas fa-times"></i> Clear Selection
        </button>
        <button 
          class="bulk-settlement-btn" 
          (click)="openBulkSettlementForm()"
          [disabled]="loading || selectedTransactionsCount === 0">
          <i class="fas fa-money-bill-wave"></i> Update Settlement
        </button>
      </div>
    </div>
  </div>

  <!-- Desktop Table View -->
  <div *ngIf="!loading && transactions.length > 0" class="desktop-table-container">
    <div class="table-wrapper">
      <table class="transactions-table">
        <thead>
          <tr>
            <th class="expand-column"></th>
            <th class="checkbox-column">
              <input 
                type="checkbox" 
                [checked]="allSelectableTransactionsSelected"
                [indeterminate]="someSelectableTransactionsSelected"
                (change)="allSelectableTransactionsSelected ? clearSelection() : selectAllTransactions()"
                class="select-all-checkbox">
            </th>
            <th>Agent Name</th>
            <th>Collection Date</th>
            <th>Total Price</th>
            <th>Settlement Status</th>
            <th>Settlement Date</th>
            <th>Settlement Medium</th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let transaction of transactions">
            <tr class="transaction-row" 
                [class.selected]="isTransactionSelected(transaction.id)"
                [class.expanded]="isRowExpanded(transaction.id)">
              <td class="expand-column">
                <button 
                  class="expand-btn"
                  (click)="toggleRow(transaction.id)"
                  [title]="isRowExpanded(transaction.id) ? 'Collapse' : 'Expand'">
                  <i class="fas" [class.fa-chevron-down]="isRowExpanded(transaction.id)" [class.fa-chevron-right]="!isRowExpanded(transaction.id)"></i>
                </button>
              </td>
              <td class="checkbox-column">
                <input 
                  type="checkbox" 
                  [checked]="isTransactionSelected(transaction.id)"
                  [disabled]="transaction.isSettled"
                  (change)="toggleTransactionSelection(transaction.id)"
                  class="transaction-checkbox"
                  [class.disabled-checkbox]="transaction.isSettled">
              </td>
              <td class="agent-name">{{ transaction.agentName }}</td>
              <td class="collection-date">{{ formatDate(transaction.collectionDate) }}</td>
              <td class="amount">{{ formatCurrency(transaction.totalNonScrapPrice) }}</td>
              <td>
                <span class="settlement-badge" [class.settled]="transaction.isSettled" [class.not-settled]="!transaction.isSettled">
                  {{ transaction.isSettled ? 'Settled' : 'Not Settled' }}
                </span>
              </td>
              <td class="settlement-date">{{ formatDate(transaction.settlementDate) }}</td>
              <td class="settlement-medium">{{ transaction.settlementMedium || 'N/A' }}</td>
            </tr>
            <!-- Expanded Details Row -->
            <tr *ngIf="isRowExpanded(transaction.id)" class="details-row">
              <td colspan="8" class="details-cell">
                <div class="details-content">
                  <div class="details-header">
                    <h4>Transaction Details</h4>
                    <div class="details-info">
                      <span><strong>Transaction ID:</strong> {{ transaction.id }}</span>
                      <span><strong>Collection ID:</strong> {{ transaction.scrapCollectionId }}</span>
                      <span *ngIf="transaction.settlementNotes"><strong>Settlement Notes:</strong> {{ transaction.settlementNotes }}</span>
                    </div>
                  </div>
                  <div class="items-table-container">
                    <table class="items-table">
                      <thead>
                        <tr>
                          <th>Item Name</th>
                          <th>Unit</th>
                          <th>Quantity</th>
                          <th>Deducted</th>
                          <th>Net Quantity</th>
                          <th>Unit Price</th>
                          <th>Total Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr *ngFor="let item of transaction.nonScrapItems">
                          <td>{{ item.scrapItemName }}</td>
                          <td>{{ item.unit }}</td>
                          <td>{{ formatNumber(item.quantity, 2) }}</td>
                          <td>{{ formatNumber(item.deductedQuantity, 2) }}</td>
                          <td>{{ formatNumber(item.netQuantity, 2) }}</td>
                          <td>{{ formatCurrency(item.unitPrice) }}</td>
                          <td><strong>{{ formatCurrency(item.totalPrice) }}</strong></td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr class="items-total-row">
                          <td colspan="6"><strong>Total:</strong></td>
                          <td><strong>{{ formatCurrency(transaction.totalNonScrapPrice) }}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </td>
            </tr>
          </ng-container>
          <!-- Page Totals Row -->
          <tr class="transaction-row totals-row page-totals">
            <td class="expand-column"></td>
            <td class="checkbox-column"></td>
            <td class="agent-name"><strong>Page Totals</strong></td>
            <td class="collection-date"></td>
            <td class="amount"><strong>{{ formatCurrency(totalPageAmount) }}</strong></td>
            <td></td>
            <td class="settlement-date"></td>
            <td class="settlement-medium"></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Mobile Card View -->
  <div *ngIf="!loading && transactions.length > 0" class="mobile-cards-container">
    <div *ngFor="let transaction of transactions" class="transaction-card"
         [class.selected]="isTransactionSelected(transaction.id)">
      <div class="card-header">
        <div class="card-header-left">
          <input 
            type="checkbox" 
            [checked]="isTransactionSelected(transaction.id)"
            [disabled]="transaction.isSettled"
            (change)="toggleTransactionSelection(transaction.id)"
            class="mobile-checkbox"
            [class.disabled-checkbox]="transaction.isSettled">
          <h3>{{ transaction.agentName }}</h3>
        </div>
        <span class="collection-date">{{ formatDate(transaction.collectionDate) }}</span>
      </div>
      
      <div class="card-content">
        <div class="info-row">
          <span class="label">Total Price:</span>
          <span class="value">{{ formatCurrency(transaction.totalNonScrapPrice) }}</span>
        </div>
        
        <div class="info-row">
          <span class="label">Settlement Status:</span>
          <span class="value">
            <span class="settlement-badge" [class.settled]="transaction.isSettled" [class.not-settled]="!transaction.isSettled">
              {{ transaction.isSettled ? 'Settled' : 'Not Settled' }}
            </span>
          </span>
        </div>
        
        <div class="info-row" *ngIf="transaction.settlementDate">
          <span class="label">Settlement Date:</span>
          <span class="value">{{ formatDate(transaction.settlementDate) }}</span>
        </div>
        
        <div class="info-row" *ngIf="transaction.settlementMedium">
          <span class="label">Settlement Medium:</span>
          <span class="value">{{ transaction.settlementMedium }}</span>
        </div>

        <!-- Expandable Details -->
        <div class="expandable-section">
          <button 
            class="expand-btn-mobile"
            (click)="toggleRow(transaction.id)">
            <i class="fas" [class.fa-chevron-down]="isRowExpanded(transaction.id)" [class.fa-chevron-right]="!isRowExpanded(transaction.id)"></i>
            {{ isRowExpanded(transaction.id) ? 'Hide Details' : 'Show Details' }}
          </button>
          
          <div *ngIf="isRowExpanded(transaction.id)" class="mobile-details">
            <div class="mobile-details-header">
              <p><strong>Transaction ID:</strong> {{ transaction.id }}</p>
              <p><strong>Collection ID:</strong> {{ transaction.scrapCollectionId }}</p>
              <p *ngIf="transaction.settlementNotes"><strong>Settlement Notes:</strong> {{ transaction.settlementNotes }}</p>
            </div>
            <div class="mobile-items-list">
              <div *ngFor="let item of transaction.nonScrapItems" class="mobile-item-card">
                <h5>{{ item.scrapItemName }}</h5>
                <div class="item-details">
                  <span>Qty: {{ formatNumber(item.quantity, 2) }} {{ item.unit }}</span>
                  <span>Deducted: {{ formatNumber(item.deductedQuantity, 2) }}</span>
                  <span>Net: {{ formatNumber(item.netQuantity, 2) }}</span>
                  <span>Price: {{ formatCurrency(item.totalPrice) }}</span>
                </div>
              </div>
            </div>
            <div class="mobile-total">
              <strong>Total: {{ formatCurrency(transaction.totalNonScrapPrice) }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- No Data State -->
  <div *ngIf="!loading && transactions.length === 0" class="no-data-state">
    <div class="no-data-content">
      <i class="fas fa-box-open"></i>
      <h3>No Non-Scrap Transactions Found</h3>
      <p>No transactions match your current filters.</p>
    </div>
  </div>

  <!-- Pagination -->
  <div *ngIf="!loading && transactions.length > 0" class="pagination-container">
    <app-pagination
      [page]="currentPage"
      [totalPages]="totalPages"
      [pageSize]="pageSize"
      (pageChange)="onPageChange($event)"
      (pageSizeChange)="onPageSizeChange($event)">
    </app-pagination>
  </div>

  <!-- Bulk Settlement Form Modal -->
  <div *ngIf="showBulkSettlementForm" class="modal-overlay" (click)="closeBulkSettlementForm()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>Update Settlement</h2>
        <button class="close-btn" (click)="closeBulkSettlementForm()">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="form-info">
          <p><strong>{{ selectedTransactionsCount }}</strong> transaction(s) selected</p>
          <p>Total Amount: <strong>{{ formatCurrency(selectedTransactionsTotalAmount) }}</strong></p>
        </div>
        
        <form class="bulk-settlement-form" (ngSubmit)="submitBulkSettlement()">
          <div class="form-group">
            <label for="settlementDate">Settlement Date *</label>
            <input 
              type="date" 
              id="settlementDate"
              [(ngModel)]="bulkSettlementForm.settlementDate"
              name="settlementDate"
              class="form-control"
              required>
          </div>
          
          <div class="form-group">
            <label for="settlementMedium">Settlement Medium *</label>
            <input 
              type="text" 
              id="settlementMedium"
              [(ngModel)]="bulkSettlementForm.settlementMedium"
              name="settlementMedium"
              class="form-control"
              placeholder="e.g., UPI, Bank Transfer, Cash"
              required>
          </div>
          
          <div class="form-group">
            <label for="settlementNotes">Settlement Notes</label>
            <textarea 
              id="settlementNotes"
              [(ngModel)]="bulkSettlementForm.settlementNotes"
              name="settlementNotes"
              class="form-control"
              placeholder="Enter settlement details..."
              rows="3"></textarea>
          </div>
          
          <div class="form-actions">
            <button 
              type="button" 
              class="cancel-btn"
              (click)="closeBulkSettlementForm()"
              [disabled]="bulkSettlementLoading">
              Cancel
            </button>
            <button 
              type="submit" 
              class="submit-btn"
              [disabled]="bulkSettlementLoading">
              <span *ngIf="bulkSettlementLoading" class="loading-spinner-small"></span>
              {{ bulkSettlementLoading ? 'Updating...' : 'Update Settlement' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>
